import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';

import { SparklineChart } from './SparklineChart.js';

export interface KpiStripStat {
  key: string;
  label: string;
  value: string | number;
  suffix?: string;
  /** Percent change versus the previous period; undefined hides the delta. */
  trend?: number;
  /** `count` renders the delta as an absolute number (prototype: «6 не подтверждены»). */
  trendKind?: 'percent' | 'count';
  /** i18n key for the foot note rendered next to the delta. */
  noteKey?: string;
  /** Real daily series behind the KPI (last 30 days); absent → no sparkline. */
  spark?: number[];
}

export interface KpiStripProps {
  stats: readonly KpiStripStat[];
  testId?: string;
}

/**
 * Warm Precision KPI strip (spec D `.kpis`): one surface, hairline dividers,
 * tabular numbers — not a row of separate cards. Icons are deliberately absent
 * (spec D hides `.kpi-icon`); the label/value/foot rhythm carries the
 * hierarchy. Deltas and sparklines render only when the server series
 * honestly produce them — no fabricated trends.
 */
export function KpiStrip({ stats, testId }: KpiStripProps): ReactElement {
  const { t, locale } = useTranslation();
  const numberLocale = locale === 'en' ? 'en-US' : 'ru-RU';

  const formatDelta = (metric: KpiStripStat): string => {
    if (metric.trendKind === 'count') {
      return new Intl.NumberFormat(numberLocale, {
        signDisplay: 'always',
      }).format(metric.trend ?? 0);
    }
    return new Intl.NumberFormat(numberLocale, {
      style: 'percent',
      signDisplay: 'always',
      maximumFractionDigits: 1,
    }).format((metric.trend ?? 0) / 100);
  };

  return (
    <section
      data-testid={testId}
      aria-label={t('crm.dashboard.kpi.section_label')}
      className="grid grid-cols-2 gap-px overflow-hidden sm:grid-cols-3 xl:grid-cols-6"
      style={{
        background: 'var(--ant-color-border-secondary, #e7e5e4)',
        border: '1px solid var(--ant-color-border-secondary, #e7e5e4)',
        borderRadius: 'var(--ant-border-radius-lg, 14px)',
      }}
    >
      {stats.map((metric) => (
        <div
          key={metric.key}
          className="crm-kpi-cell"
          style={{
            background: 'var(--ant-color-bg-container, #fffdfa)',
            padding: '12px 16px',
            minWidth: 0,
          }}
        >
          <div
            className="truncate"
            style={{
              fontSize: 10.5,
              fontWeight: 550,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              color: 'var(--ant-color-text-tertiary, #6d6963)',
            }}
          >
            {metric.label}
          </div>
          <div className="flex items-baseline gap-1" style={{ margin: '4px 0 2px', minHeight: 26 }}>
            <span
              data-testid={`dashboard-kpi-${metric.key}`}
              className="truncate"
              style={{
                fontSize: 22,
                fontWeight: 600,
                letterSpacing: '-0.02em',
                color: 'var(--ant-color-text, #292524)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {metric.value}
            </span>
            {metric.suffix ? (
              <span
                className="shrink-0"
                style={{
                  fontSize: 12,
                  fontWeight: 550,
                  color: 'var(--ant-color-text-tertiary, #6d6963)',
                }}
              >
                {metric.suffix}
              </span>
            ) : null}
          </div>
          {metric.trend !== undefined || (metric.spark !== undefined && metric.spark.length > 1) ? (
            <div className="flex items-center gap-2">
              {metric.trend !== undefined ? (
                <span
                  className="inline-flex items-center gap-0.5"
                  style={{
                    fontSize: 11,
                    fontWeight: 550,
                    // Semantic tokens (not --ant-*): antd's cssVar scope
                    // overrides the latter inside its subtree with a muted
                    // dark value that fails AA on the strip surface.
                    color:
                      metric.trend > 0
                        ? 'var(--color-success, #15803d)'
                        : metric.trend < 0
                          ? 'var(--color-error, #b91c1c)'
                          : 'var(--color-warning, #c2410c)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {metric.trendKind === 'count'
                    ? ''
                    : metric.trend > 0
                      ? '↑'
                      : metric.trend < 0
                        ? '↓'
                        : '•'}{' '}
                  {formatDelta(metric)}
                </span>
              ) : null}
              {metric.trend !== undefined && metric.noteKey ? (
                <span
                  className="truncate"
                  style={{ fontSize: 11, color: 'var(--ant-color-text-tertiary, #6d6963)' }}
                >
                  {t(metric.noteKey)}
                </span>
              ) : null}
              {metric.spark !== undefined && metric.spark.length > 1 ? (
                <span
                  className="ml-auto shrink-0"
                  style={{
                    color: 'var(--ant-color-primary-border-hover, #d97706)',
                    opacity: 0.8,
                  }}
                >
                  <SparklineChart data={metric.spark} width={56} height={18} strokeWidth={1.5} />
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ))}
    </section>
  );
}
